import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DiYii } from "react-icons/di";
import { useForm } from "react-hook-form";

const EditTemplate = () => {
  const { templateId } = useParams();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaFile, setMediaFile] = useState(null);
  const [bodyContent, setBodyContent] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const accessToken = process.env.REACT_APP_FACEBOOK_TOKEN;
  const apiVersion = "v22.0";

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!accessToken) {
        console.error("Access token is missing!");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://graph.facebook.com/${apiVersion}/${templateId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const templateData = response.data;

        setTemplate(templateData);
        setValue("templateName", templateData.name || "");
        setValue("language", templateData.language || "en");
        setValue("headerType", templateData.components?.[0]?.format || "None");
        setValue("footerContent", templateData.components?.[2]?.text || "");

        // Set Body Content Correctly
        const bodyText = templateData.components?.[1]?.text || "";
        setBodyContent(bodyText);
        setValue("body", bodyText);
      } catch (error) {
        console.error("Error fetching template:", error);
      } finally {
        setLoading(false);
      }
    };

    if (templateId) fetchTemplate();
  }, [templateId, accessToken, setValue]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setMediaFile(fileURL);
      setValue("mediaFile", file);
    }
  };


  const onSubmit = async (data) => {
    const headerType = watch("headerType"); // Fix for undefined headerType

    if (!data.templateName || !data.body || !data.language) {
      alert("Please fill in all required fields.");
      return;
    }

    const requestBody = {
      name: `template_${Date.now()}`.toLowerCase(),
      category: "MARKETING",
      parameter_format: "NAMED",
      allow_category_change: true,
      language: data.language, // Fix: language is a string, not an object
      components: [],
    };

    // Add Header
    if (headerType !== "None") {
      const headerComponent = { type: "HEADER", format: headerType.toUpperCase() };

      if (headerType === "Text") {
        headerComponent.text = data.headerContent;
      }

      requestBody.components.push(headerComponent);
    }



    // Add Body
    requestBody.components.push({
      type: "BODY",
      text: data.body,
    });

    // Add Footer (if exists)
    if (data.footerContent) {
      requestBody.components.push({
        type: "FOOTER",
        text: data.footerContent,
      });
    }

    // console.log("Request Payload:", JSON.stringify(requestBody, null, 2));

    try {
      const response = await fetch(
        `https://graph.facebook.com/${apiVersion}/${templateId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_FACEBOOK_TOKEN}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();
      // console.log("Response:", result);

      if (response.ok) {
        alert("Template updated successfully!");
      } else {
        alert(result.error.message);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };
  if (loading) return <p>Loading template...</p>;
  if (!template) return <p>Template not found</p>;

  const headerType = watch("headerType");

  return (
    <div className="flex flex-wrap p-6 gap-8">
      {/* Left Side - Input Form */}
      <div className="w-full lg:w-1/2 bg-gray-100 p-6 rounded-lg shadow-lg">
        <div className="w-full bg-white p-4 mb-2 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4 flex gap-4">
            <DiYii size={35} className="bg-green-600 p-1 rounded-lg text-white" />
            {watch("templateName") || "your_template_name"} • {watch("language") || "English"}
          </h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Template Name & Language */}
          <div className="bg-white p-4 rounded shadow mb-4">
            <h3 className="mb-2">Template Name & Language</h3>
            <input
              {...register("templateName", { required: "Template name is required" })}
              type="text"
              className="w-full p-2 border rounded mb-2"
              placeholder="Enter template name"
            />
            {errors.templateName && <p className="text-red-500">{errors.templateName.message}</p>}

            <h3 className="mb-2">Select Language</h3>
            <select {...register("language")} className="w-full p-2 border rounded">
              {["en", "es", "fr", "de", "it", "pt"].map((code) => (
                <option key={code} value={code}>
                  {code.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Content Section */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-semibold mb-2">Content</h3>

            {/* Header Selection */}
            <label className="block mb-1">Header</label>
            <select {...register("headerType")} className="w-full p-2 border rounded mb-2">
              {["None", "Text", "Image", "Video", "Document"].map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            {headerType === "Text" && (
              <input {...register("headerContent")} type="text" className="w-full p-2 border rounded mb-2" placeholder="Enter header text" />
            )}
            {["Image", "Video", "Document"].includes(headerType) && (
              <input type="file" className="w-full p-2 border rounded mb-2" onChange={handleFileUpload} />
            )}

            {/* Body Input with Rich Text Editor */}
            <label className="block mb-1">Body</label>
            <ReactQuill theme="snow" value={bodyContent} onChange={setBodyContent} className="mb-2 bg-white" />

            {/* Footer Input */}
            <label className="block mb-1">Footer (Optional)</label>
            <input {...register("footerContent")} type="text" className="w-full p-2 border rounded" placeholder="Enter footer text" />
          </div>

          <button type="submit" className="mt-4 px-4 py-2 bg-green-500 text-white rounded">
            Update
          </button>
        </form>
      </div>

      {/* Right Side - WhatsApp Preview */}
      <div className="w-1/3 bg-cover bg-center bg-white h-28 p-4 rounded-lg shadow-lg">
        <h3 className="font-semibold mb-2">Template Preview</h3>
        <div className="p-3">
          <div
            className="bg-cover bg-center p-4"
            style={{
              backgroundImage:
                "url('https://e0.pxfuel.com/wallpapers/722/149/desktop-wallpaper-message-background-whatsapp-message-background.jpg')",
            }}
          >
            <div className="bg-white p-3 rounded-lg shadow-lg w-72">
              {/* Header Preview */}
              {headerType !== "None" && (
                <div className="font-bold mb-2">
                  {headerType === "Text" ? (
                    watch("headerContent")
                  ) : mediaFile ? (
                    <img
                      src={mediaFile}
                      alt="Uploaded"
                      className="w-full h-auto rounded"
                    />
                  ) : (
                    <p className="italic text-gray-500">
                      [ {headerType} Preview ]
                    </p>
                  )}
                </div>
              )}
              {/* Body Preview */}
              <div
                dangerouslySetInnerHTML={{ __html: bodyContent }}
                className="mb-2"
              />
              {/* Footer Preview */}
              {watch("footerContent") && (
                <p className="text-sm text-gray-500">
                  {watch("footerContent")}
                </p>
              )}
              {/* Time Simulation */}
              <p className="text-xs text-gray-400 text-right">18:10</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EditTemplate;
